import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/utils'
import { fieldGroupStatus, useFieldStatus, type FieldStatus } from './field'

/**
 * InputGroup — the shell that wraps a borderless control plus addons (icons,
 * buttons, selectors, helper rows) as a single bordered unit. Unlike `Input`
 * (which owns its own border + focus ring), the *shell* owns the border and the
 * `focus-within` ring, so the whole unit — addon, field, button — lights up
 * together. This is the foundation the `SearchBar` and future dropdown/selector
 * inputs are built on.
 *
 *   <InputGroup>
 *     <InputGroupAddon>{icon}</InputGroupAddon>
 *     <InputGroupInput placeholder="Search" />
 *     <InputGroupAddon align="trailing" divided>{button}</InputGroupAddon>
 *   </InputGroup>
 *
 * The shell is a `flex-wrap` row sized by `min-h-*`, so inline addons + the field
 * share the main row while `block-start` / `block-end` addons (`basis-full`) take
 * their own full-width rows above/below — and a multi-line `InputGroupTextarea`
 * grows the shell vertically. Reuses the same border + ring tokens as `Input` via
 * `fieldGroupStatus`. Status is inherited from a parent `<Field>` (or set via the
 * `status` prop); `size` + `disabled` are published to the inner control through
 * context.
 *
 * `overflow-hidden` clips segmented addon fills to the rounded corners; the
 * focus ring is a box-shadow so it still shows outside that clip.
 */

type InputGroupSize = 'sm' | 'md' | 'lg'

interface InputGroupContextValue {
  size: InputGroupSize
  status: FieldStatus
  disabled: boolean
}

const InputGroupContext = React.createContext<InputGroupContextValue>({
  size: 'md',
  status: 'default',
  disabled: false,
})

export const inputGroupVariants = cva(
  cn(
    'flex w-full flex-wrap items-center overflow-hidden rounded-lg border bg-fbui-base-white',
    // ring width lives in the size variants — sm gets a slimmer 2px ring so it
    // stays proportionate on the compact control; md/lg keep the 4px ring.
    'outline-none transition',
    // Dim when the whole group is disabled (the `disabled` prop sets
    // `data-disabled`), OR when the field control itself is disabled. Scoping the
    // field case to input/textarea keeps an incidental disabled addon button
    // (e.g. an empty-state submit) from tinting the shell, while `data-disabled`
    // still covers non-input fields like the contentEditable RichTextEditor.
    'data-[disabled]:cursor-not-allowed data-[disabled]:bg-fbui-gray-50',
    'has-[input:disabled]:cursor-not-allowed has-[input:disabled]:bg-fbui-gray-50',
    'has-[textarea:disabled]:cursor-not-allowed has-[textarea:disabled]:bg-fbui-gray-50'
  ),
  {
    variants: {
      status: fieldGroupStatus,
      size: {
        sm: 'min-h-8 focus-within:ring-2',
        md: 'min-h-9 focus-within:ring-4',
        lg: 'min-h-10 focus-within:ring-4',
      },
    },
    defaultVariants: { status: 'default', size: 'md' },
  }
)

export interface InputGroupProps
  extends
    Omit<React.HTMLAttributes<HTMLDivElement>, 'color'>,
    Omit<VariantProps<typeof inputGroupVariants>, 'status'> {
  /** Border + ring color. Inherited from a parent `<Field>` when omitted. */
  status?: FieldStatus
  /** Dim the shell + disable the inner control. */
  disabled?: boolean
}

export const InputGroup = React.forwardRef<HTMLDivElement, InputGroupProps>(
  (
    { className, status, size = 'md', disabled = false, children, ...props },
    ref
  ) => {
    const resolved = useFieldStatus(status ?? undefined)
    const ctx = React.useMemo(
      () => ({ size: size ?? 'md', status: resolved, disabled }),
      [size, resolved, disabled]
    )
    return (
      <InputGroupContext.Provider value={ctx}>
        <div
          ref={ref}
          data-disabled={disabled || undefined}
          className={cn(
            inputGroupVariants({ status: resolved, size }),
            className
          )}
          {...props}
        >
          {children}
        </div>
      </InputGroupContext.Provider>
    )
  }
)
InputGroup.displayName = 'InputGroup'

/**
 * The borderless control that fills the shell. Strips its own border/ring (the
 * shell provides them) and inherits text size + disabled from `InputGroup`.
 */
export const InputGroupInput = React.forwardRef<
  HTMLInputElement,
  Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>
>(({ className, type = 'text', disabled, ...props }, ref) => {
  const { size, disabled: groupDisabled } = React.useContext(InputGroupContext)
  return (
    <input
      ref={ref}
      type={type}
      disabled={disabled ?? groupDisabled}
      className={cn(
        'min-w-0 flex-1 border-0 bg-transparent px-3 text-fbui-gray-800 outline-none',
        'placeholder:text-fbui-gray-400 focus:ring-0',
        'disabled:cursor-not-allowed disabled:bg-transparent disabled:text-fbui-text-disabled',
        size === 'sm' ? 'text-xs' : 'text-sm',
        className
      )}
      {...props}
    />
  )
})
InputGroupInput.displayName = 'InputGroupInput'

/**
 * A borderless multi-line control. Spans the full width of the shell (its own
 * row), pairs naturally with `block-start` / `block-end` addons (a header
 * toolbar or a footer with a submit button), and grows the shell vertically.
 * Inherits text size + disabled from `InputGroup`.
 */
export const InputGroupTextarea = React.forwardRef<
  HTMLTextAreaElement,
  Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'>
>(({ className, disabled, rows = 3, ...props }, ref) => {
  const { size, disabled: groupDisabled } = React.useContext(InputGroupContext)
  return (
    <textarea
      ref={ref}
      rows={rows}
      disabled={disabled ?? groupDisabled}
      className={cn(
        'fbui-scroll min-w-0 basis-full resize-y border-0 bg-transparent px-3 py-2 text-fbui-gray-800 outline-none',
        'placeholder:text-fbui-gray-400 focus:ring-0',
        'disabled:cursor-not-allowed disabled:bg-transparent disabled:text-fbui-text-disabled',
        size === 'sm' ? 'text-xs' : 'text-sm',
        className
      )}
      {...props}
    />
  )
})
InputGroupTextarea.displayName = 'InputGroupTextarea'

const addonVariants = cva('flex shrink-0 items-center text-fbui-gray-500', {
  variants: {
    align: {
      leading: '',
      trailing: '',
      /** Full-width row above the field — a header, label, or toolbar. */
      'block-start': 'basis-full px-3 py-2',
      /** Full-width row below the field — a counter, hint, or footer actions. */
      'block-end': 'basis-full px-3 py-2',
    },
    /**
     * `divided` segments the addon from the field with a 1px rule and stretches
     * it full-height (inline) or full-width (block), flush to the shell's edge
     * (the shell has no padding, so it sits at the very edge). The rule is a
     * step lighter than the shell's `gray-300` border so a header/footer or
     * segment reads as a subtle separation, not a hard box. Use for a leading
     * selector, a trailing button, or a block header/footer. `false` is a padded
     * wrapper for a decorative icon or inline content.
     */
    divided: { true: 'border-fbui-gray-200', false: '' },
  },
  compoundVariants: [
    // Inline decorative addons pad only their outer edge — the input owns its
    // own px-3 on the inner side, so padding both would double the icon↔text gap.
    { divided: false, align: 'leading', className: 'pl-3' },
    { divided: false, align: 'trailing', className: 'pr-3' },
    { divided: true, align: 'leading', className: 'self-stretch border-r' },
    { divided: true, align: 'trailing', className: 'self-stretch border-l' },
    { divided: true, align: 'block-start', className: 'border-b' },
    { divided: true, align: 'block-end', className: 'border-t' },
  ],
  defaultVariants: { align: 'leading', divided: false },
})

export interface InputGroupAddonProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof addonVariants> {}

/**
 * Slot for content beside or around the field. Inline (`leading` / `trailing`)
 * sits in the main row; `block-start` / `block-end` take their own full-width
 * row above/below. Default (`divided={false}`) is a padded wrapper for a
 * decorative icon; `divided` segments a button/selector/header from the field
 * with a divider, flush to the shell edge.
 */
export const InputGroupAddon = React.forwardRef<
  HTMLDivElement,
  InputGroupAddonProps
>(({ className, align, divided, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(addonVariants({ align, divided }), className)}
    {...props}
  />
))
InputGroupAddon.displayName = 'InputGroupAddon'

export const inputGroupButtonVariants = cva(
  cn(
    'inline-flex shrink-0 items-center justify-center gap-1.5 rounded-md font-medium',
    'text-fbui-gray-500 outline-none transition-colors',
    'hover:bg-fbui-gray-100 hover:text-fbui-gray-700',
    'focus-visible:ring-2 focus-visible:ring-fbui-ring-default',
    'disabled:pointer-events-none disabled:text-fbui-text-disabled',
    '[&_svg]:shrink-0'
  ),
  {
    variants: {
      size: {
        xs: 'h-6 px-2 text-xs [&_svg]:size-3.5',
        sm: 'h-8 px-3 text-xs [&_svg]:size-4',
        'icon-xs': 'size-6 [&_svg]:size-3.5',
        'icon-sm': 'size-8 [&_svg]:size-4',
      },
    },
    defaultVariants: { size: 'xs' },
  }
)

export interface InputGroupButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof inputGroupButtonVariants> {}

/**
 * A compact, low-emphasis button sized to sit inside the shell — a clear (×), a
 * password reveal, a copy action, or a small inline submit. Renders as
 * `type="button"` and inherits `disabled` from the group. Use `icon-xs` /
 * `icon-sm` for icon-only buttons (and give them an `aria-label`). For a
 * prominent filled submit, place a regular `<Button>` in a `divided` addon.
 */
export const InputGroupButton = React.forwardRef<
  HTMLButtonElement,
  InputGroupButtonProps
>(({ className, size, type, disabled, ...props }, ref) => {
  const { disabled: groupDisabled } = React.useContext(InputGroupContext)
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      disabled={disabled ?? groupDisabled}
      className={cn(inputGroupButtonVariants({ size }), className)}
      {...props}
    />
  )
})
InputGroupButton.displayName = 'InputGroupButton'
